//TODO : Refactor after ai
/** Modul 2 – Kunden: Liste mit Potenzial und 360°-Ansicht einer Kommune. */
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDatenStore } from "@app/store";
import {
  datensatz,
  datensaetze,
  rolle as rolleVon,
} from "@shared/demo-data/viacid";
import {
  angebotsSumme,
  crossSellingPotenziale,
  datumCH,
  fr,
  mit,
  naechsteSaison,
  str,
  umsatzSplit,
  ziel,
  neueId,
} from "./lib";
import { Hinweis, Karte, Kennzahl, Knopf, Pille, Seite, Zeile } from "./ui";
import { ActionLeiste } from "./ActionLeiste";
import { objektKlasse as klasseVon } from "@shared/demo-data/viacid";

export function KundenListePage() {
  const b = useDatenStore((s) => s.bestand);
  const nav = useNavigate();
  const [suche, setSuche] = useState("");
  const pot = crossSellingPotenziale(b);
  const kunden = datensaetze(b, "kunde").filter((k) =>
    `${k.titel} ${k.untertitel}`.toLowerCase().includes(suche.toLowerCase())
  );
  return (
    <Seite
      titel="Kunden"
      untertitel="Kommunen, Händler und Unternehmen mit Umsatzsplit und Cross-Selling-Potenzial"
      aktionen={
        <input
          className="vc-btn"
          placeholder="Suchen …"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          style={{ minWidth: 220 }}
        />
      }
    >
      <Karte>
        <table className="vc-table">
          <thead>
            <tr>
              <th>Kunde</th>
              <th>Gruppe</th>
              <th>Betreut von</th>
              <th className="num">Dienstleistung</th>
              <th className="num">Baustoffe</th>
              <th>Baustoffbezug</th>
              <th>Potenzial</th>
            </tr>
          </thead>
          <tbody>
            {kunden.map((k) => {
              const u = umsatzSplit(b, k.id);
              const p = pot.find((x) => x.kunde.id === k.id);
              return (
                <tr
                  key={k.id}
                  className="link"
                  onClick={() => nav(`/app/kunden/${k.id}`)}
                >
                  <td>
                    <strong>{k.titel}</strong>
                    <div className="sub">{k.kennung}</div>
                  </td>
                  <td>{str(k.werte.kundengruppe)}</td>
                  <td>
                    {ziel(
                      b,
                      k,
                      "kunde-wird-betreut-von-mitarbeiter",
                      "mitarbeiter"
                    )?.titel ?? "—"}
                  </td>
                  <td className="num">{fr(u.dienst)}</td>
                  <td className="num">{fr(u.handel)}</td>
                  <td>{str(k.werte.baustoffbezug) || "unbekannt"}</td>
                  <td>{p ? <Pille farbe="teal">{p.art}</Pille> : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Karte>
    </Seite>
  );
}

export function Kunde360Page() {
  const { id = "" } = useParams();
  const b = useDatenStore((s) => s.bestand);
  const rolle = useDatenStore((s) => s.rolle);
  const anlegen = useDatenStore((s) => s.anlegen);
  const nav = useNavigate();
  const k = datensatz(b, "kunde", id);
  if (!k) return <Hinweis ton="red">Kunde nicht gefunden.</Hinweis>;

  const u = umsatzSplit(b, k.id);
  const gesamt = u.dienst + u.handel;
  const pot = crossSellingPotenziale(b).find((x) => x.kunde.id === k.id);
  const { saison, wochen, laeuft } = naechsteSaison();
  const ansprechpartner = mit(
    b,
    "ansprechpartner",
    "ansprechpartner-gehoert-zu-kunde",
    k.id
  );
  const strassen = mit(
    b,
    "strassenobjekt",
    "strassenobjekt-gehoert-zu-kunde",
    k.id
  );
  const chancen = mit(
    b,
    "verkaufschance",
    "verkaufschance-betrifft-kunde",
    k.id
  );
  const angebote = mit(b, "angebot", "angebot-an-kunde", k.id);
  const auftraege = mit(b, "auftrag", "auftrag-von-kunde", k.id);
  const kontakte = mit(
    b,
    "kundenkontakt",
    "kundenkontakt-betrifft-kunde",
    k.id
  ).sort((x, y) => str(y.werte.datum).localeCompare(str(x.werte.datum)));
  const aufgaben = mit(b, "aufgabe", "aufgabe-betrifft-kunde", k.id).filter(
    (a) => a.status === "Offen"
  );
  const betreuer = ziel(
    b,
    k,
    "kunde-wird-betreut-von-mitarbeiter",
    "mitarbeiter"
  );

  const chanceAnlegen = () => {
    const persona = rolleVon(rolle)?.persona ?? betreuer?.id ?? null;
    const nid = neueId("vc");
    const art = pot?.art ?? "Dienstleistung";
    anlegen(
      "verkaufschance",
      {
        id: nid,
        titel: `${saison.massnahmen[0]} ${saison.name} – ${k.titel}`,
        untertitel: `${art} · ${saison.fenster}`,
        kennung: null,
        status: "Identifiziert",
        werte: {
          bezeichnung: `${saison.massnahmen[0]} ${saison.name} – ${k.titel}`,
          geschaeftsbereich: art,
          akquiseanlass: pot ? "Cross-Selling" : "Saisonimpuls",
          bedarfsbeschreibung:
            pot?.grund ?? `Saisonimpuls ${saison.name} in ${wochen} Wochen`,
          saisonfenster: saison.fenster,
          geschaetztesVolumen: 0,
        },
        bezuege: {
          "verkaufschance-betrifft-kunde": k.id,
          ...(persona
            ? { "verkaufschance-zugewiesen-an-mitarbeiter": persona }
            : {}),
        },
      },
      "Verkaufschance angelegt."
    );
    nav(`/app/chancen/${nid}`);
  };

  return (
    <Seite
      titel={k.titel}
      untertitel={`${str(k.werte.kundengruppe)} · ${str(k.werte.adresse)} · betreut von ${betreuer?.titel ?? "—"}`}
      aktionen={
        <>
          <Knopf onClick={() => nav(`/app/daten/kunde/${k.id}`)}>
            Stammdaten
          </Knopf>
          <Knopf art="primary" onClick={chanceAnlegen}>
            Verkaufschance anlegen
          </Knopf>
          {klasseVon("kunde") && (
            <ActionLeiste
              klasse={klasseVon("kunde")!}
              satz={k}
              ausser={["Verkaufschance anlegen"]}
              verwandt={[
                { slug: "kundenkontakt", rel: "kundenkontakt-betrifft-kunde" },
                { slug: "aufgabe", rel: "aufgabe-betrifft-kunde" },
                { slug: "reklamation", rel: "reklamation-von-kunde" },
                {
                  slug: "strassenobjekt",
                  rel: "strassenobjekt-gehoert-zu-kunde",
                },
              ]}
            />
          )}
        </>
      }
    >
      {pot && (
        <div style={{ marginBottom: 16 }}>
          <Hinweis>
            <strong>Cross-Selling-Potenzial {pot.art}:</strong> {k.titel}{" "}
            {pot.grund}. {saison.name}-Saison{" "}
            {laeuft ? "läuft seit" : "beginnt in"} {wochen} Wochen – passende{" "}
            {pot.art === "Baustoffe"
              ? saison.baustoffe.join(", ")
              : saison.massnahmen.join(", ")}
            .
          </Hinweis>
        </div>
      )}
      <div className="vc-grid c4" style={{ marginBottom: 16 }}>
        <Kennzahl
          label="Umsatz gesamt"
          wert={fr(gesamt)}
          hint="aus Aufträgen (Beispiel)"
        />
        <Kennzahl
          label="Dienstleistung"
          wert={gesamt ? `${Math.round((100 * u.dienst) / gesamt)} %` : "—"}
          hint={fr(u.dienst)}
          ton="accent"
        />
        <Kennzahl
          label="Baustoffe"
          wert={gesamt ? `${Math.round((100 * u.handel) / gesamt)} %` : "—"}
          hint={fr(u.handel)}
        />
        <Kennzahl
          label="Offene Angebote"
          wert={
            angebote.filter((a) =>
              ["Versendet", "In Erstellung", "Änderung gewünscht"].includes(
                a.status ?? ""
              )
            ).length
          }
        />
      </div>

      <div className="vc-grid main-side">
        <div className="vc-grid">
          <Karte titel="Verkaufschancen">
            <div className="vc-list">
              {chancen.map((c) => (
                <Zeile
                  key={c.id}
                  titel={c.titel}
                  meta={`${str(c.werte.geschaeftsbereich)} · ${str(c.werte.saisonfenster)} · geschätzt ${fr(Number(c.werte.geschaetztesVolumen) || 0)}`}
                  rechts={<Pille status={c.status} />}
                  zu={`/app/chancen/${c.id}`}
                />
              ))}
              {chancen.length === 0 && (
                <div className="sub">Noch keine Verkaufschance.</div>
              )}
            </div>
          </Karte>
          <Karte titel="Angebote und Aufträge">
            <div className="vc-list">
              {angebote.map((a) => (
                <Zeile
                  key={a.id}
                  titel={`${a.kennung} · ${a.titel}`}
                  meta={`${str(a.werte.dokumentart)} · ${datumCH(a.werte.angebotsdatum)} · ${fr(angebotsSumme(b, a.id))}`}
                  rechts={<Pille status={a.status} />}
                  zu={`/app/angebote/${a.id}`}
                />
              ))}
              {auftraege.map((a) => (
                <Zeile
                  key={a.id}
                  titel={`${a.kennung} · ${a.titel}`}
                  meta={`${str(a.werte.auftragsart)} · ${datumCH(a.werte.auftragsdatum)}`}
                  rechts={<Pille status={a.status} />}
                  zu={`/app/auftraege/${a.id}`}
                />
              ))}
            </div>
          </Karte>
          <Karte titel="Strassenobjekte" sub="Objekte dieses Kunden">
            <div className="vc-list">
              {strassen.map((s) => (
                <Zeile
                  key={s.id}
                  titel={s.titel}
                  meta={`${str(s.werte.adresse)} · ${str(s.werte.belagsart)}`}
                  zu={`/app/daten/strassenobjekt/${s.id}`}
                />
              ))}
            </div>
          </Karte>
        </div>
        <div className="vc-grid">
          <Karte titel="Ansprechpartner">
            <div className="vc-list">
              {ansprechpartner.map((a) => (
                <Zeile
                  key={a.id}
                  titel={a.titel}
                  meta={`${str(a.werte.funktion)} · ${str(a.werte.telefon)}`}
                />
              ))}
            </div>
          </Karte>
          <Karte
            titel="Nächste Schritte"
            sub={`${aufgaben.length} offene Aufgaben`}
          >
            <div className="vc-list">
              {aufgaben.map((a) => (
                <Zeile
                  key={a.id}
                  titel={a.titel}
                  meta={`fällig ${datumCH(a.werte.faelligAm)}`}
                  zu={`/app/daten/aufgabe/${a.id}`}
                />
              ))}
              {aufgaben.length === 0 && (
                <div className="sub">Kein Follow-up geplant.</div>
              )}
            </div>
          </Karte>
          <Karte titel="Historie" sub="Besuche, Telefonate, E-Mails">
            <div className="vc-list">
              {kontakte.map((c) => (
                <Zeile
                  key={c.id}
                  titel={`${str(c.werte.kontaktart)} · ${str(c.werte.anlass)}`}
                  meta={`${datumCH(c.werte.datum)} · ${str(c.werte.ergebnis)}`}
                />
              ))}
              {kontakte.length === 0 && (
                <div className="sub">Noch kein Kontakt dokumentiert.</div>
              )}
            </div>
          </Karte>
        </div>
      </div>
    </Seite>
  );
}
